import React, { useEffect, useState, useCallback } from "react";
import styled from "styled-components";
import Text from "../../../components/atoms/Text";
import Button from "../../../components/atoms/Button";
import { DateRangePicker } from "../../../components/molecules/DateRangePicker";
import { getDateStringFromToday } from "../../../utils/TimeUtil";
import { AdminConsultationRecordTable } from "../../../components/templates/AdminConsultationRecordTemplate/AdminConsultationRecordTable";
import {
  fetchAdminConsultationRecords,
  AdminConsultationListParams,
} from "../../../api/consultationService";
import { ToastManager } from "../../../components/organisms/CommonUI/ToastPopup/ToastManager";
import { WaitingManager } from "../../../components/organisms/CommonUI/WaitingPopup/WaitingManager";
import {
  ConsultationRecordAdmin,
  ConsultationRecordSummary,
} from "common/models/types";
import DownloadUtil from "../../../utils/DownloadUtil";

const Container = styled.div`
  width: 100%;
  max-width: 100%;
`;

const Header = styled.div`
  margin-bottom: 32px;
`;

const Title = styled(Text)`
  font-size: 24px;
  font-weight: 600;
  color: #333;
`;

const FilterSection = styled.div`
  margin-bottom: 24px;
  padding: 20px;
  background: #f9fafb;
  border-radius: 8px;
  width: 100%;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const FilterLabel = styled(Text)`
  min-width: 80px;
  font-weight: 500;
  color: #666;
`;

const DateFilterWrapper = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex: 1;
`;

const QuickDateButton = styled.button<{ active: boolean }>`
  padding: 6px 12px;
  border: 1px solid ${(props) => (props.active ? "#4A90E2" : "#ddd")};
  background: ${(props) => (props.active ? "#4A90E2" : "#fff")};
  color: ${(props) => (props.active ? "#fff" : "#666")};
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;

  &:hover {
    border-color: #4a90e2;
    color: ${(props) => (props.active ? "#fff" : "#4A90E2")};
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #4a90e2;
  }
`;

const CSVButton = styled(Button)`
  margin-left: auto;
  min-width: 140px;
`;

const SummarySection = styled.div`
  display: flex;
  gap: 24px;
  padding: 16px 20px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SummaryLabel = styled(Text)`
  font-size: 12px;
  color: #6b7280;
`;

const SummaryValue = styled(Text)`
  font-size: 18px;
  font-weight: 600;
  color: #374151;
`;

const StatusSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  min-width: 120px;

  &:focus {
    outline: none;
    border-color: #4a90e2;
  }
`;

const AdminConsultationRecordsPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState<ConsultationRecordAdmin[]>([]);
  const [summary, setSummary] = useState<ConsultationRecordSummary | null>(null);

  // 필터 상태
  const [startDate, setStartDate] = useState(getDateStringFromToday({ days: -7 }));
  const [endDate, setEndDate] = useState(getDateStringFromToday());
  const [quickDateRange, setQuickDateRange] = useState("1week");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // 페이지네이션 상태
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);

  // 날짜 빠른 선택
  const handleQuickDateSelect = (range: string) => {
    setQuickDateRange(range);
    const today = getDateStringFromToday();

    switch (range) {
      case "today":
        setStartDate(today);
        setEndDate(today);
        break;
      case "1week":
        setStartDate(getDateStringFromToday({ days: -7 }));
        setEndDate(today);
        break;
      case "1month":
        setStartDate(getDateStringFromToday({ days: -30 }));
        setEndDate(today);
        break;
      case "3months":
        setStartDate(getDateStringFromToday({ days: -90 }));
        setEndDate(today);
        break;
    }
  };

  // 날짜 변경 핸들러
  const handleDateChange = (name: "start" | "end", date: Date) => {
    const dateString = date.toISOString().split("T")[0];
    if (name === "start") {
      setStartDate(dateString);
    } else {
      setEndDate(dateString);
    }
    setQuickDateRange("");
  };

  // 데이터 로드
  const loadRecords = useCallback(async () => {
    try {
      setLoading(true);
      WaitingManager.show({ message: "상담 기록을 불러오는 중..." });

      const params: AdminConsultationListParams = {
        startDate,
        endDate,
        status: statusFilter,
        userId: searchKeyword || undefined,
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
      };

      const response = await fetchAdminConsultationRecords(params);

      if (response.error === 0 && response.data) {
        setRecords(response.data.records);
        setTotal(response.data.total);
        setSummary(response.data.summary);
      } else {
        ToastManager.show({ content: response.message || "데이터를 불러오는데 실패했습니다." });
      }
    } catch (error) {
      console.error("Failed to load consultation records:", error);
      ToastManager.show({ content: "상담 기록을 불러오는데 실패했습니다." });
    } finally {
      setLoading(false);
      WaitingManager.hide();
    }
  }, [startDate, endDate, statusFilter, searchKeyword, currentPage, pageSize]);

  // 상세보기 팝업
  const handleViewDetail = (recordId: number) => {
    const width = Math.min(1200, window.screen.width * 0.8);
    const height = Math.min(900, window.screen.height * 0.85);
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    window.open(
      `/admin/consultation-records/${recordId}/popup`,
      `consultation-record-${recordId}`,
      `width=${width},height=${height},left=${left},top=${top},` +
        `toolbar=no,location=no,directories=no,status=no,menubar=no,` +
        `scrollbars=yes,resizable=yes`
    );
  };

  // CSV 다운로드
  const handleDownloadCSV = () => {
    try {
      if (records.length === 0) {
        ToastManager.show({ content: "다운로드할 데이터가 없습니다." });
        return;
      }

      const csvHeader = "ID,사용자,상태,처리시간(초),비용($),생성일\n";
      const csvData = records.map((record) => {
        const processingTime = record.analysis?.processingTimeMs
          ? Math.floor(record.analysis.processingTimeMs / 1000)
          : "";
        const cost = record.analysis?.cost?.total?.toFixed(3) || "";
        const status = getStatusText(record.status);
        const createdAt = record.createdAt
          ? new Date(record.createdAt).toLocaleDateString("ko-KR")
          : "";

        return `${record.id},"${record.userName || ""}",${status},${processingTime},${cost},${createdAt}`;
      }).join("\n");

      const csv = csvHeader + csvData;
      const filename = `상담기록_${startDate}_${endDate}.csv`;

      DownloadUtil.downloadCsv(csv, filename);

      ToastManager.show({
        content: `${records.length}건의 데이터를 다운로드했습니다.`,
      });
    } catch (error) {
      console.error("CSV 다운로드 실패:", error);
      ToastManager.show({ content: "CSV 다운로드에 실패했습니다." });
    }
  };

  // 상태 텍스트 변환
  const getStatusText = (status: number): string => {
    switch (status) {
      case -1:
        return "실패";
      case 0:
        return "대기중";
      case 100:
        return "STT처리중";
      case 200:
        return "LLM처리중";
      case 300:
        return "완료";
      default:
        return "알수없음";
    }
  };

  // 처리 시간 포맷팅
  const formatProcessingTime = (ms?: number): string => {
    if (!ms) return "-";
    const seconds = Math.floor(ms / 1000);
    return `${seconds}초`;
  };

  // 초기 로드 및 필터 변경 시 재로드
  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  // 페이지 변경 시 맨 위로
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);

  return (
    <Container>
      <Header>
        <Title>상담 녹음 기록 관리</Title>
      </Header>

      <FilterSection>
        <FilterRow>
          <FilterLabel>기간 선택</FilterLabel>
          <DateFilterWrapper>
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onChangeDate={handleDateChange}
              disabled={loading}
            />
            <QuickDateButton
              active={quickDateRange === "today"}
              onClick={() => handleQuickDateSelect("today")}
            >
              오늘
            </QuickDateButton>
            <QuickDateButton
              active={quickDateRange === "1week"}
              onClick={() => handleQuickDateSelect("1week")}
            >
              1주일
            </QuickDateButton>
            <QuickDateButton
              active={quickDateRange === "1month"}
              onClick={() => handleQuickDateSelect("1month")}
            >
              1개월
            </QuickDateButton>
            <QuickDateButton
              active={quickDateRange === "3months"}
              onClick={() => handleQuickDateSelect("3months")}
            >
              3개월
            </QuickDateButton>
            <CSVButton
              size="Medium"
              type="Text"
              variant="Contained"
              color="Primary"
              onClick={handleDownloadCSV}
              disabled={loading || records.length === 0}
            >
              CSV 다운로드 ({total}건)
            </CSVButton>
          </DateFilterWrapper>
        </FilterRow>

        <FilterRow>
          <FilterLabel>상태</FilterLabel>
          <StatusSelect
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="all">전체</option>
            <option value="pending">대기중</option>
            <option value="processing">처리중</option>
            <option value="completed">완료</option>
            <option value="failed">실패</option>
          </StatusSelect>

          <FilterLabel style={{ marginLeft: 24 }}>검색</FilterLabel>
          <SearchInput
            type="text"
            placeholder="사용자 ID로 검색..."
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setCurrentPage(1);
                loadRecords();
              }
            }}
          />
        </FilterRow>
      </FilterSection>

      {summary && (
        <SummarySection>
          <SummaryItem>
            <SummaryLabel>총 기록</SummaryLabel>
            <SummaryValue>{summary.totalRecords}건</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>완료</SummaryLabel>
            <SummaryValue style={{ color: "#10b981" }}>
              {summary.completedRecords}건
            </SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>실패</SummaryLabel>
            <SummaryValue style={{ color: "#ef4444" }}>
              {summary.failedRecords}건
            </SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>대기중</SummaryLabel>
            <SummaryValue style={{ color: "#6b7280" }}>
              {summary.pendingRecords}건
            </SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>평균 처리시간</SummaryLabel>
            <SummaryValue>{formatProcessingTime(summary.avgProcessingTimeMs)}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>총 비용</SummaryLabel>
            <SummaryValue>${summary.totalCost?.toFixed(2) || "0.00"}</SummaryValue>
          </SummaryItem>
        </SummarySection>
      )}

      <AdminConsultationRecordTable
        records={records}
        loading={loading}
        currentPage={currentPage}
        pageSize={pageSize}
        total={total}
        onPageChange={setCurrentPage}
        onViewDetail={handleViewDetail}
      />
    </Container>
  );
};

export default AdminConsultationRecordsPage;
